import { forwardRef, useImperativeHandle, useEffect, useRef, useState } from 'react';
import { ConfirmGuard, ConfirmTone } from '@/components/deposits/ConfirmGuard';

export interface ConfirmDialogOptions {
  title: string;
  description?: string;
  warning?: string;
  notice?: string;
  confirmLabel?: string;
  tone?: ConfirmTone;
}

export interface ConfirmDialogHandle {
  // window.confirm 대체: 다이얼로그를 열고 사용자의 선택을 Promise<boolean>으로 돌려준다.
  // 취소/ESC/오버레이 닫힘 = false. 호출부는 async 흐름에서 await 한 줄로 게이트.
  confirm: (opts: ConfirmDialogOptions) => Promise<boolean>;
}

// window.confirm → ConfirmGuard 다이얼로그 브리지 컨테이너 (Home 소유 단일 인스턴스, ref 주입).
// open state는 여기(컨테이너)가 소유 — Home 재렌더 없이 열림(P11 CombinedShippingModalContainer 선례).
// ⚠ React.memo 금지(CLAUDE.md 규율 3 — stale 함정).
export const ConfirmDialogContainer = forwardRef<ConfirmDialogHandle>(function ConfirmDialogContainer(_props, ref) {
  const [pending, setPending] = useState<{ opts: ConfirmDialogOptions; resolve: (ok: boolean) => void } | null>(null);
  // resolve 소유권은 ref가 진실(동기 기록) — state는 렌더용. 렌더 전 중첩 호출/언마운트에서도
  // Promise가 누수 없이 정리되도록 confirm/settle이 ref를 즉시 갱신한다(codexloop R11).
  const pendingRef = useRef<typeof pending>(null);

  useImperativeHandle(ref, () => ({
    confirm: (opts) =>
      new Promise<boolean>((resolve) => {
        // 모달이 페이지 입력을 막아 중첩 호출은 정상 경로에선 없음 — 방어적으로 이전 건 false 정리.
        pendingRef.current?.resolve(false);
        const next = { opts, resolve };
        pendingRef.current = next;
        setPending(next);
      }),
  }), []);

  // 언마운트 시 미해결 Promise가 호출부 await를 영원히 매달지 않게 false로 정리.
  useEffect(() => () => { pendingRef.current?.resolve(false); pendingRef.current = null; }, []);

  const settle = (ok: boolean) => {
    pendingRef.current?.resolve(ok);
    pendingRef.current = null;
    setPending(null);
  };

  return (
    <ConfirmGuard
      open={pending !== null}
      title={pending?.opts.title ?? ''}
      description={pending?.opts.description}
      warning={pending?.opts.warning}
      notice={pending?.opts.notice}
      confirmLabel={pending?.opts.confirmLabel ?? '확인'}
      tone={pending?.opts.tone ?? 'warn'}
      loading={false}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );
});
